/**
 * useCompare — up to 4 products selected for side-by-side comparison.
 *
 * State persists in localStorage (`socibaba.compare.ids`) so the tray
 * survives a page reload but is per-device (no server sync — comparisons
 * are ephemeral). Exposes:
 *   - ids: string[]     — ordered array (max 4)
 *   - count             — ids.length
 *   - isCompared(id)    — membership
 *   - add(id)           — no-op if already present or full
 *   - remove(id)
 *   - toggle(id)        — flip; returns 'added' | 'removed' | 'full'
 *   - clear()
 */
import { useCallback, useEffect, useState } from "react";

const KEY = "socibaba.compare.ids";
const MAX = 4;
const BUS = "socibaba.compare.changed";

function readIds(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string").slice(0, MAX) : [];
  } catch {
    return [];
  }
}

function writeIds(ids: string[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids));
    window.dispatchEvent(new Event(BUS));
  } catch {}
}

export function useCompare() {
  const [ids, setIds] = useState<string[]>(readIds);

  useEffect(() => {
    // Sync when another card in the same tab mutates the tray.
    const onChange = () => setIds(readIds());
    window.addEventListener(BUS, onChange);
    // Also sync across tabs
    const onStorage = (e: StorageEvent) => { if (e.key === KEY) setIds(readIds()); };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(BUS, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const isCompared = useCallback((id: string) => ids.includes(id), [ids]);

  const add = useCallback((id: string) => {
    if (ids.includes(id) || ids.length >= MAX) return;
    writeIds([...ids, id]);
  }, [ids]);

  const remove = useCallback((id: string) => {
    if (!ids.includes(id)) return;
    writeIds(ids.filter((x) => x !== id));
  }, [ids]);

  const toggle = useCallback((id: string): "added" | "removed" | "full" => {
    if (ids.includes(id)) {
      writeIds(ids.filter((x) => x !== id));
      return "removed";
    }
    if (ids.length >= MAX) return "full";
    writeIds([...ids, id]);
    return "added";
  }, [ids]);

  const clear = useCallback(() => writeIds([]), []);

  return { ids, count: ids.length, max: MAX, isCompared, add, remove, toggle, clear };
}
